import type { FC } from 'react';
import { useForm } from 'react-hook-form@latest';
import { ErrorMessage } from '@hookform/error-message';

import { errorToast, preventNumberScroll, successToast } from '@/helpers';
import { _notNil } from '@/littledash';

import type { Study } from '@/model/Study.model';

import { useRequest } from '@/support/Hooks/request';

import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

import { generateCages } from './AddCages.utils';

interface AddCagesProps {
  study?: Pick<Study, 'id' | 'name'>;
  handleCallback?: (response: unknown) => void;
  closeModal: () => void;
}

interface AddCagesFormData {
  cages: number;
}

const MINIMUM_CAGES = 1;
const MAXIMUM_CAGES = 50;

const AddCages: FC<AddCagesProps> = ({ study, handleCallback, closeModal }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<AddCagesFormData>({
    defaultValues: {
      cages: 1,
    },
  });

  const { sendRequest: createCages, requestSending: creatingCages } = useRequest({
    route: 'cages.createMany',
    method: 'post',
  });

  const onSubmit = async ({ cages }: AddCagesFormData) => {
    const studyId = _notNil(study?.id) ? Number(study?.id) : null;
    try {
      const res = await createCages({ cages: generateCages(studyId, cages) });
      successToast(`Successfully created ${cages} cage${cages !== 1 ? 's' : ''}`);
      handleCallback?.(res);
    } catch {
      errorToast('Could not create cages');
    }
  };

  const title = _notNil(study) ? `Create cages in study ${study.name}` : 'Create cages';

  return (
    <ModalContainer size="narrow" className="bg-white">
      <ModalHeader
        title={title}
        closeModal={closeModal}
        closeDisabled={creatingCages}
        className="pa3 bb b--moon-gray w-100"
      />
      <div className="pa3 mv3">
        <label htmlFor="cages">How many cages would you like to create?</label>
        <input
          type="number"
          id="cages"
          data-testid="add-cages-input"
          onWheel={preventNumberScroll}
          autoFocus
          style={{ marginBottom: 0 }}
          {...register('cages', {
            valueAsNumber: true,
            min: {
              value: MINIMUM_CAGES,
              message: `Can't be less than ${MINIMUM_CAGES}`,
            },
            max: {
              value: MAXIMUM_CAGES,
              message: `Can't be more than ${MAXIMUM_CAGES}`,
            },
            required: 'This field is required',
          })}
        />
        <ErrorMessage
          errors={errors}
          name="cages"
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        submitBtnText="Create"
        cancelBtnText="Cancel"
        submitButtonProps={{
          disabled: creatingCages,
          loading: creatingCages,
        }}
        cancelButtonProps={{ disabled: creatingCages }}
      />
    </ModalContainer>
  );
};

export default AddCages;
